import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { formatDecimalValueNumberInput, formatIntValueNumberInput } from '@/core/helpers/form';

type FormInputNumberControllerProps = TextFieldProps & {
  controllerName: string;
  error?: boolean;
  errorText?: string;
  helperText?: string;
  fieldOnBlur?: () => void;
  decimalPlaces?: number;
  isWholeNumber?: boolean;
  valueTransformer?: (value: string) => string;
};

const formatNumberForDisplay = (value: string, decimalPlaces: number = 6): string => {
  // console.log(value);
  if (value === undefined || value === null) return '';

  return Number.isInteger(Number(value))
    ? value.toString()
    : Number(value)
        .toFixed(decimalPlaces)
        .replace(/\.?0+$/, '');
};

export const FormInputNumberController = ({
  controllerName,
  error,
  errorText,
  helperText,
  fieldOnBlur,
  decimalPlaces,
  isWholeNumber = false,
  valueTransformer,
  ...props
}: FormInputNumberControllerProps) => {
  const { control } = useFormContext();
  const handleChange = (value: string, onChange: (value: number | undefined) => void) => {
    const cleanedValue = isWholeNumber
      ? formatIntValueNumberInput(value)
      : formatDecimalValueNumberInput(value, decimalPlaces);
    onChange(cleanedValue as unknown as number);
  };

  return (
    <Controller
      name={controllerName}
      control={control}
      render={({ field: { value, onChange, ...field } }) => {
        const displayValue = valueTransformer ? valueTransformer(value) : value;
        return (
          <TextField
            {...field}
            value={formatNumberForDisplay(displayValue, decimalPlaces)}
            type="string"
            onChange={(event) => handleChange(event.target.value, onChange)}
            onBlur={() => {
              field.onBlur();
              fieldOnBlur?.();
            }}
            helperText={(error && errorText) || helperText}
            error={error}
            fullWidth
            {...props}
          />
        );
      }}
    />
  );
};
